import { useQuery } from '@tanstack/react-query';
import { Dayjs } from 'dayjs';
import { QueryKey } from '../../enums/QueryKey';
import {
    getAvailableAndBookedBookings,
    getAllAvailableBookingsByDateAndWorktype,
    getTotalBookedBookingsByDateRangeAndWorktype,
    getAllBookedBookingsByDateRangeGroupByDateAndLocation,
    getOldestAndLatestBookedBookingsByDateAndWorktype,
    getAllBookedBookingsByDateRangeWorktypeAndLocation,
    getSumOfBookedBookingsByDateAndLocationGroupByWorktype,
    getSumOfAllBookedBookingsByDateRangeWorktypeAndLocation,
    getAllBookedBookingsByDateRangeAndWorktype,
    getSumOfBookedBookingsByDateAndWorktypeGroupByLocation,
} from '../../services/bookingDelta';
import dayjs from '../../utils/dayjs';
import { appTimezone } from '../../config';
import { ifDateLocationAndWorktype } from '../../utils/componentUtils';

export function useAllAvailableBookingsByDateAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data, refetch, isFetchedAfterMount, isFetching, isLoading } = useQuery({
        queryKey: [
            QueryKey.AllAvailableBookingsByDateAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getAllAvailableBookingsByDateAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: [],
    });

    return { data, refetch, loading: isFetchedAfterMount ? isLoading : isFetching };
}

export function useAvailableAndBookedBookings({
    date,
    location,
    worktype,
}: {
    date: Dayjs;
    location: number;
    worktype: number;
}) {
    const dateFromStr = dayjs(date).tz(appTimezone).startOf('month').toISOString();
    const dateToStr = dayjs(date).tz(appTimezone).endOf('month').toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.AvailableAndBookedBookings,
            { dateFrom: dateFromStr, dateTo: dateToStr, location, worktype },
        ],
        queryFn: () => getAvailableAndBookedBookings({ dateFrom: dateFromStr, dateTo: dateToStr, location, worktype }),
        enabled: ifDateLocationAndWorktype({ date, location, worktype }),
        initialData: [],
    });

    return { data };
}

export function useTotalBookedBookingsByDateRangeAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.TotalBookedBookingsByDateRangeAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getTotalBookedBookingsByDateRangeAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: {
            totalBookings: {
                positive: 0,
                negative: 0,
            },
        },
    });

    return data;
}

export function useAllBookedBookingsByDateRangeGroupByDateAndLocation({
    dateFrom,
    dateTo,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [QueryKey.AllAvailableBookingsByDateAndWorktype, { dateFrom: dateFromStr, dateTo: dateToStr }],
        queryFn: () => getAllBookedBookingsByDateRangeGroupByDateAndLocation(dateFromStr, dateToStr),
        enabled: !!dateFrom && !!dateTo,
        initialData: [],
    });

    return data;
}

export function useOldestAndLatestBookedBookingsByDateAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.OldestAndLatestBookedBookingsByDateAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getOldestAndLatestBookedBookingsByDateAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: [],
    });

    return data;
}

export function useAllBookedBookingsByDateRangeWorktypeAndLocation({
    dateFrom,
    dateTo,
    worktype,
    location,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
    location: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.AllBookedBookingsByDateRangeWorktypeAndLocation,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype, location },
        ],
        queryFn: () => getAllBookedBookingsByDateRangeWorktypeAndLocation(dateFromStr, dateToStr, worktype, location),
        enabled: !!dateFrom && !!dateTo && !!worktype && !!location,
        initialData: [],
    });

    return [data];
}

export function useTotalBookedBookingsByDateAndLocationGroupByWorktype({
    dateFrom,
    dateTo,
    location,
    recordCreatedAt,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    location: number;
    recordCreatedAt: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.TotalBookedBookingsByDateAndLocationGroupByWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, location, recordCreatedAt },
        ],
        queryFn: () =>
            getSumOfBookedBookingsByDateAndLocationGroupByWorktype(dateFromStr, dateToStr, location, recordCreatedAt),
        enabled: !!dateFrom && !!dateTo && !!location && !!recordCreatedAt,
        initialData: [],
    });

    return [data];
}

export function useTotalBookedBookingsByDateRangeAndWorktypeAndLocation({
    dateFrom,
    dateTo,
    worktype,
    location,
    recordCreatedAt,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
    location: number;
    recordCreatedAt: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.TotalBookedBookingsByDateRangeAndWorktypeAndLocation,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype, location, recordCreatedAt },
        ],
        queryFn: () =>
            getSumOfAllBookedBookingsByDateRangeWorktypeAndLocation(
                dateFromStr,
                dateToStr,
                worktype,
                location,
                recordCreatedAt
            ),
        enabled: !!dateFrom && !!dateTo && !!worktype && !!location,
        initialData: {
            positive: 0,
            negative: 0,
        },
    });

    return data;
}

export function useAllBookedBookingsByDateRangeAndWorktype({
    dateFrom,
    dateTo,
    worktype,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.AllBookedBookingsByDateRangeAndWorktype,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype },
        ],
        queryFn: () => getAllBookedBookingsByDateRangeAndWorktype(dateFromStr, dateToStr, worktype),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: [],
    });

    return [data];
}

export function useSumOfBookedBookingsByDateAndWorktypeGroupByLocation({
    dateFrom,
    dateTo,
    worktype,
    recordCreatedAt,
}: {
    dateFrom: Dayjs;
    dateTo: Dayjs;
    worktype: number;
    recordCreatedAt: number;
}) {
    const dateFromStr = dateFrom?.toISOString();
    const dateToStr = dateTo?.toISOString();

    const { data } = useQuery({
        queryKey: [
            QueryKey.SumOfBookedBookingsByDateAndWorktypeGroupByLocation,
            { dateFrom: dateFromStr, dateTo: dateToStr, worktype, recordCreatedAt },
        ],
        queryFn: () =>
            getSumOfBookedBookingsByDateAndWorktypeGroupByLocation(dateFromStr, dateToStr, worktype, recordCreatedAt),
        enabled: !!dateFrom && !!dateTo && !!worktype,
        initialData: [],
    });

    return [data];
}
